import React, { useCallback } from "react";
import {
  Avatar,
  Button,
  Container,
  CssBaseline,
  TextField,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { LockOutlined } from "@material-ui/icons";
import { Controller, useForm } from "react-hook-form";
import { Redirect, useHistory } from "react-router-dom";
import { useAppDispatch, useTypedSelector } from "../../app/store";
import { cancelTwoFactor, contractLoginRecoveryCode } from "./loginSlice";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      marginTop: theme.spacing(8),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.secondary.main,
    },
    form: {
      width: "100%",
      marginTop: theme.spacing(1),
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
  })
);

interface RecoveryCodeFormValues {
  recoveryCode: string;
}

export function normalizeRecoveryCode(value: string): string {
  const compact = value.replace(/-/g, "").toUpperCase();
  return compact.length === 10
    ? `${compact.slice(0, 5)}-${compact.slice(5)}`
    : value.toUpperCase();
}

export function RecoveryCodePage() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const state = useTypedSelector((x) => x.login);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<RecoveryCodeFormValues>({
    mode: "all",
    defaultValues: { recoveryCode: "" },
  });

  const onSubmit = useCallback(
    async (values: RecoveryCodeFormValues) => {
      await dispatch(
        contractLoginRecoveryCode({
          recoveryCode: normalizeRecoveryCode(values.recoveryCode),
        })
      );
    },
    [dispatch]
  );

  const onCancel = useCallback(() => {
    dispatch(cancelTwoFactor());
    history.replace("/login");
  }, [dispatch, history]);

  if (state.loggedIn) {
    return (
      <Redirect
        to={
          state.loggedInWithRecoveryCode
            ? "/login/recovery-code/recommendation"
            : "/contract"
        }
      />
    );
  }
  if (!state.pendingAccessToken || !state.requiresTwoFactor) {
    return <Redirect to="/login" />;
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlined />
        </Avatar>
        <Typography component="h1" variant="h5">
          リカバリーコードを
          <br />
          使用してログイン
        </Typography>
        <form
          className={classes.form}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Typography component="p" variant="body1">
            認証アプリを使用できない場合、二要素認証設定時に発行されたリカバリーコードを使用してログインすることができます。
            <br />
            それぞれのリカバリーコードは1度のみ使用できます。
          </Typography>
          <Controller
            control={control}
            name="recoveryCode"
            rules={{ required: "リカバリーコードを入力してください" }}
            render={({ field }) => {
              return <TextField
                {...field}
                label="リカバリーコード"
                placeholder="XXXXX-XXXXX"
                variant="outlined"
                margin="normal"
                inputProps={{ maxLength: 11 }}
                error={!!errors.recoveryCode}
                helperText={errors.recoveryCode?.message}
                fullWidth
                autoFocus
              />
            }}
          />
          <Button
            className={classes.submit}
            color="primary"
            variant="contained"
            type="submit"
            disabled={!isValid || isSubmitting}
            fullWidth
          >
            ログイン
          </Button>
          <Button
            variant="contained"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onCancel}
            fullWidth
          >
            キャンセル
          </Button>
        </form>
      </div>
    </Container>
  );
}
